var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Connection } from "./Connection.js";
import { Resource } from "./Resource.js";
import { Bkper } from "./Bkper.js";
import * as ConnectionService from "../service/connection-service.js";
import * as UserService from "../service/user-service.js";
import { Billing } from "./Billing.js";
/**
 * This class defines a User on the Bkper platform.
 *
 * Users can own and collaborate on [[Books]], manage [[Collections]], and connect to external services through [[Connections]].
 *
 * Each User has a unique identity, subscription plan details, and access permissions across the platform.
 *
 * @public
 */
export class User extends Resource {
    constructor(payload, config) {
        super(payload);
        this.config = config;
    }
    /** @internal */
    getConfig() {
        return this.config || Bkper.globalConfig;
    }
    /**
     * Gets the id of the User.
     *
     * @returns The User's id
     */
    getId() {
        return this.payload.id;
    }
    /**
     * Gets the given name of the User.
     *
     * @returns The User's given name
     */
    getGivenName() {
        return this.payload.givenName;
    }
    /**
     * Gets the name of the User.
     *
     * @returns The User's name
     */
    getName() {
        return this.payload.name;
    }
    /**
     * Gets the avatar url of the User.
     *
     * @returns The User's avatar url
     */
    getAvatarUrl() {
        return this.payload.avatarUrl;
    }
    /**
     * Gets the full name of the User.
     *
     * @returns The User's full name
     */
    getFullName() {
        return this.payload.fullName;
    }
    /**
     * Gets the email of the User.
     *
     * @returns The User's email
     */
    getEmail() {
        return this.payload.email;
    }
    /**
     * Gets the hosted domain of the User.
     *
     * @returns The User's hosted domain
     */
    getHostedDomain() {
        return this.payload.hostedDomain;
    }
    /**
     * Gets the billing cycle of the User's current plan.
     *
     * @returns The User's plan billing cycle
     */
    getPlanCycle() {
        return this.payload.planCycle;
    }
    /**
     * Gets the username of the User.
     *
     * @returns The User's username
     */
    getUsername() {
        return this.payload.username;
    }
    /**
     * Gets the billing information for this User.
     *
     * @returns The User's billing information
     */
    getBilling() {
        return __awaiter(this, void 0, void 0, function* () {
            const payload = yield UserService.getBilling(this.getConfig());
            return new Billing(payload, this.config);
        });
    }
    /**
     * Tells if the User has already used [[Connections]].
     *
     * @returns True if the User has already used Connections
     */
    hasUsedConnections() {
        return this.payload.bankConnections;
    }
    /**
     * Gets the [[Connections]] of the User.
     *
     * @returns The retrieved Connection objects
     */
    getConnections() {
        return __awaiter(this, void 0, void 0, function* () {
            const json = yield ConnectionService.listConnections(this.getConfig());
            return json.map((c) => new Connection(c, this.config));
        });
    }
    /**
     * Gets a [[Connection]] of the User.
     *
     * @param id - The Connection's id
     *
     * @returns The retrieved Connection object
     */
    getConnection(id) {
        return __awaiter(this, void 0, void 0, function* () {
            const json = yield ConnectionService.getConnection(id, this.getConfig());
            return new Connection(json, this.config);
        });
    }
}
//# sourceMappingURL=User.js.map